/**
 * PostHog Logger Integration Component
 *
 * Registers PostHog instance with the logger utility so errors
 * and events can be tracked outside of React components.
 * Also performs health checks and diagnostics.
 * Identifies the current user (and role) when session is set for feature-flag targeting.
 */

import { useEffect, useRef } from 'react';
import { usePostHog } from 'posthog-js/react';
import { useAppStore } from '../../store';
import { logger, registerPostHog } from '@repo/shared-utils';
import { verifyPostHogHealth, testPostHogCapture, testPostHogExceptionCapture, logPostHogHealth, registerPostHogForVerification } from '@repo/shared-utils';

export function PostHogLogger() {
  const posthog = usePostHog();
  const healthCheckIntervalRef = useRef<number | null>(null);
  const hasPerformedInitialCheck = useRef(false);
  const clerkSignedIn = useAppStore((s) => s.clerkSignedIn);
  const currentUserId = useAppStore((s) => s.currentUserId);
  const userRole = useAppStore((s) => s.userRole);
  const isWebTest = import.meta.env.MODE === 'test';
  const hasBackendProfile = Boolean(currentUserId?.trim());

  // Identify user in PostHog when signed in with a backend profile (Vitest: store-only).
  useEffect(() => {
    if (!posthog) return;
    const shouldIdentify = hasBackendProfile && (isWebTest || clerkSignedIn);
    if (shouldIdentify) {
      posthog.identify(currentUserId);
      posthog.register({ role: userRole });
    } else {
      posthog.reset();
    }
  }, [posthog, clerkSignedIn, currentUserId, userRole, hasBackendProfile, isWebTest]);

  useEffect(() => {
    // Register PostHog instance with logger when available
    if (posthog) {
      registerPostHog(posthog);
      registerPostHogForVerification(posthog);
      
      // Perform initial health check
      if (!hasPerformedInitialCheck.current) {
        hasPerformedInitialCheck.current = true;
        
        // Wait a bit for PostHog to fully initialize
        setTimeout(() => {
          logPostHogHealth();

          // Never emit synthetic test events/exceptions in production telemetry.
          if (import.meta.env.DEV) {
            testPostHogCapture().then((success) => {
              if (success) {
                logger.info('[PostHog] Capture test successful', { module: 'PostHogLogger', action: 'capture_test' });
              } else {
                logger.warn('[PostHog] Capture test failed - PostHog may not be working correctly', { module: 'PostHogLogger', action: 'capture_test' });
              }
            });

            testPostHogExceptionCapture().then((success) => {
              if (success) {
                logger.info('[PostHog] Exception capture test successful', { module: 'PostHogLogger', action: 'exception_capture_test' });
              } else {
                logger.warn('[PostHog] Exception capture test failed - error tracking may not work', { module: 'PostHogLogger', action: 'exception_capture_test' });
              }
            });
          }
        }, 1000);
      }
      
      // Log successful registration
      if (import.meta.env.DEV) {
        logger.info('[PostHog] Logger integration registered successfully', { module: 'PostHogLogger', action: 'register' });
      }
      
      // Set up periodic health checks (every 5 minutes in production, every minute in dev)
      if (!healthCheckIntervalRef.current) {
        const intervalMs = import.meta.env.DEV ? 60000 : 300000; // 1 min dev, 5 min prod
        
        healthCheckIntervalRef.current = window.setInterval(() => {
          const health = verifyPostHogHealth();
          
          if (!health.isAvailable || !health.canCaptureException) {
            logger.warn('[PostHog] Health check failed', {
              module: 'PostHogLogger',
              action: 'health_check',
              isAvailable: health.isAvailable,
              canCaptureException: health.canCaptureException,
              error: health.error,
            });
          } else if (import.meta.env.DEV) {
            // Only log success in dev to avoid console spam
            logger.debug('[PostHog] Health check passed', { module: 'PostHogLogger', action: 'health_check' });
          }
        }, intervalMs);
      }
    } else {
      // Warn if PostHog is not available (helps debug production issues)
      logger.warn('[PostHog] PostHog instance not available. Check environment variables and initialization.', { module: 'PostHogLogger', action: 'posthog_unavailable' });
    }
    
    // Cleanup interval on unmount
    return () => {
      if (healthCheckIntervalRef.current) {
        clearInterval(healthCheckIntervalRef.current);
        healthCheckIntervalRef.current = null;
      }
    };
  }, [posthog]);

  // This component doesn't render anything
  return null;
}

